// ═══════════════════════════════════════════════════════════════════════
// agenticAIData.js — Agentic AI for CLM design & configuration data
// ═══════════════════════════════════════════════════════════════════════
// Sources: CLM_Agentic_AI.pptx
//   · 27 agents across 7 CLM zones
//   · 6 autonomy levels (L0–L5)
//   · 12 inviolable human-control points
//   · 3 governance bodies (ARB, Ethics & Safety, Model Risk)
//   · 8 KPIs (4 agent + 4 program)
//   · 5 implementation phases (24 months)
// ═══════════════════════════════════════════════════════════════════════

export const AUTONOMY_LEVELS = [
  { id: "L0", name: "Assist",      humanPct: 100, agentPct: 0,    color: "#94a3b8",
    desc: "Agent suggests; human does every action. No write tools.",
    example: "Knowledge bot in read-only mode for sensitive areas." },
  { id: "L1", name: "Draft",       humanPct: 95,  agentPct: 5,    color: "#38a8ff",
    desc: "Agent drafts; human reviews and submits every output. Default for new agents.",
    example: "WI Generator drafts; supervisor edits and publishes." },
  { id: "L2", name: "Act-Review",  humanPct: 70,  agentPct: 30,   color: "#00d4c0",
    desc: "Agent acts on routine cases; human reviews exceptions and batches. Approval before commits.",
    example: "NCR Triage handles routine dispositions; QM reviews critical." },
  { id: "L3", name: "Act-Audit",   humanPct: 30,  agentPct: 70,   color: "#00ff88",
    desc: "Agent acts within policy envelope; human audits after the fact via sampling and alerts.",
    example: "Anomaly Detector raises alerts; human reviews flagged patterns." },
  { id: "L4", name: "Adapt",       humanPct: 10,  agentPct: 90,   color: "#ffd600",
    desc: "Agent acts and tunes its own thresholds within meta-policy. Human approves meta-changes.",
    example: "Tag Mapping (after maturity) self-calibrates thresholds." },
  { id: "L5", name: "Autonomous",  humanPct: 1,   agentPct: 100,  color: "#ff4f8b",
    desc: "Full domain autonomy. Reserved for cases with bounded consequence and proven track record.",
    example: "Reserved · requires CEO + CISO + ARB approval · no defaults." },
];

export const AGENT_ZONES = [
  { id: "PLM",   label: "PLM (Engineering)",         color: "#a78bfa", icon: "📐" },
  { id: "ERP",   label: "ERP (Transactional)",        color: "#60a5fa", icon: "💼" },
  { id: "MES",   label: "MES (Manufacturing)",        color: "#fb923c", icon: "⚙️" },
  { id: "SFC",   label: "SFC + Edge (Shop Floor)",    color: "#4ade80", icon: "🔌" },
  { id: "QUAL",  label: "Quality",                   color: "#ff4f8b", icon: "🔬" },
  { id: "CLOUD", label: "Cloud / Analytics",          color: "#2dd4bf", icon: "☁️" },
  { id: "B2B",   label: "B2B / IT Ops",              color: "#fbbf24", icon: "📡" },
];

// 27 agents across 7 zones
export const AGENTS = [
  // ─── PLM zone ─────────────────────────────────────────────────────────
  { id: "AG-001", zone: "PLM", name: "Design Assistant", defaultLevel: "L1",
    role: "Suggests reuse, flags duplicates, drafts ECN justifications.",
    tools: ["search_tc_items", "compare_revisions", "draft_ecn", "find_similar_part"],
    escalations: "On any safety-critical char change OR any cross-program impact.",
    pilot: true, kpi: "% drafts accepted ≥ 85%" },
  { id: "AG-002", zone: "PLM", name: "ECN Triage", defaultLevel: "L2",
    role: "Classifies incoming ECNs by scope, urgency, and downstream blast radius.",
    tools: ["classify_ecn", "fetch_dependencies", "estimate_impact", "route_to_approver"],
    escalations: "Critical-safety items + cross-plant impact + BOM structural changes.",
    kpi: "Classification accuracy ≥ 98%" },
  { id: "AG-003", zone: "PLM", name: "BOM Reconciler", defaultLevel: "L1",
    role: "Compares EBOM/MBOM/STKO; flags mismatches; proposes resolutions.",
    tools: ["fetch_ebom", "fetch_mbom", "fetch_stko", "propose_reconciliation"],
    escalations: "Any structural delta; high-value parts.",
    kpi: "Reconciliation drafts approved ≥ 90%" },
  { id: "AG-004", zone: "PLM", name: "CAD Steward", defaultLevel: "L1",
    role: "Detects orphaned datasets, broken geometry links, missing metadata.",
    tools: ["scan_datasets", "validate_geometry", "fix_metadata"],
    escalations: "Any item-level metadata change.",
    kpi: "Orphan rate < 0.1%" },

  // ─── ERP zone ─────────────────────────────────────────────────────────
  { id: "AG-005", zone: "ERP", name: "Order Orchestration", defaultLevel: "L2",
    role: "Validates inbound customer orders; resolves exceptions; drafts production orders.",
    tools: ["get_customer", "get_atp", "validate_order", "draft_aufnr", "open_approval"],
    escalations: "Credit hold · ATP miss · pricing override · new customer.",
    kpi: "Order auto-resolve rate ≥ 80%" },
  { id: "AG-006", zone: "ERP", name: "Variance Analyst", defaultLevel: "L3",
    role: "Continuously explains cost/qty/schedule variances; drafts narrative for finance.",
    tools: ["get_actual_vs_standard", "query_lot_genealogy", "draft_variance_report"],
    escalations: "Variance > threshold · period-end · suspected data quality issue.",
    kpi: "Variance narrative accuracy ≥ 95%" },
  { id: "AG-007", zone: "ERP", name: "MDM Steward", defaultLevel: "L1",
    role: "Detects duplicate masters; proposes merges; scrubs attribute quality; publishes deltas.",
    tools: ["fuzzy_match_master", "propose_merge", "validate_attributes", "publish_mdm_delta"],
    escalations: "Always — merges & creates require human steward approval.",
    pilot: true, kpi: "Merge proposal accepted ≥ 92%" },
  { id: "AG-008", zone: "ERP", name: "Period Close Helper", defaultLevel: "L2",
    role: "Drives close checklist; validates GL postings; drafts journal entries; catches anomalies pre-close.",
    tools: ["run_close_check", "validate_gl", "draft_journal", "query_open_items"],
    escalations: "Reconciliation gap · adjusting entry · audit-flagged item.",
    kpi: "Close-prep time -40%" },

  // ─── MES zone ─────────────────────────────────────────────────────────
  { id: "AG-009", zone: "MES", name: "Dispatch", defaultLevel: "L2",
    role: "Optimally sequences work orders to lines/stations honoring constraints.",
    tools: ["fetch_orders", "fetch_capacity", "sequence", "release_to_floor"],
    escalations: "Real-time tactical decisions · floor reversibility high · customer priority needs gate.",
    kpi: "Schedule adherence ≥ 95%" },
  { id: "AG-010", zone: "MES", name: "WI Generator", defaultLevel: "L1",
    role: "Drafts work instructions from BoP + design data; multi-language; safety-aware.",
    tools: ["fetch_bop", "fetch_design_dataset", "generate_wi", "translate_wi"],
    escalations: "Operator-facing content · legal/safety language · multi-language drift risk.",
    pilot: true, kpi: "WI accepted by operator ≥ 90%" },
  { id: "AG-011", zone: "MES", name: "NCR Triage", defaultLevel: "L2",
    role: "Classifies NCRs; recommends dispositions; routes to disposition owner.",
    tools: ["classify_ncr", "recommend_disposition", "fetch_containment", "open_capa"],
    escalations: "On routine class — still L1 on critical defects · disposition is human (quality + legal) decision.",
    kpi: "Disposition recommendation accepted ≥ 85%" },
  { id: "AG-012", zone: "MES", name: "Andon Triage", defaultLevel: "L3",
    role: "Routes Andon calls to the right responder; predicts escalation severity.",
    tools: ["classify_andon", "match_responder", "page_responder"],
    escalations: "Time-critical · agent acts on routing · human owns ground-truth response.",
    kpi: "False-positive routing < 5%" },
  { id: "AG-013", zone: "MES", name: "Containment Coordinator", defaultLevel: "L2",
    role: "Manages quality holds and material containment after defects detected.",
    tools: ["create_hold", "scope_containment", "notify_stakeholders", "release_hold"],
    escalations: "Always for safety-critical containment scope.",
    kpi: "Containment scope precision ≥ 99%" },

  // ─── SFC + Edge zone ──────────────────────────────────────────────────
  { id: "AG-014", zone: "SFC", name: "Anomaly Detector", defaultLevel: "L3",
    role: "ML-on-OPC-UA-telemetry; flags drift, vibration, energy anomalies.",
    tools: ["query_tags", "score_anomaly", "raise_alert"],
    escalations: "ML output is signal not action · precision controls escalation · safety-tag exclusions.",
    kpi: "Precision on critical anomalies ≥ 80%" },
  { id: "AG-015", zone: "SFC", name: "Tag Mapping", defaultLevel: "L1",
    role: "Maps new PLC tags to canonical model; proposes naming conventions.",
    tools: ["scan_new_tags", "match_canonical", "propose_mapping"],
    escalations: "Affects every KPI downstream · one bad mapping cascades broadly.",
    kpi: "Mapping accepted on first review ≥ 90%" },
  { id: "AG-016", zone: "SFC", name: "Energy Optimizer", defaultLevel: "L2",
    role: "Recommends energy savings (start/stop, set-point, schedule shifts).",
    tools: ["query_energy", "model_savings", "propose_setpoint"],
    escalations: "Production-impacting changes · sustainability team review.",
    kpi: "kWh/unit improvement ≥ 5% per quarter" },
  { id: "AG-017", zone: "SFC", name: "Maintenance Predictor", defaultLevel: "L2",
    role: "Predicts machine failures; recommends preventive jobs; books slots.",
    tools: ["query_telemetry", "predict_failure", "book_pm"],
    escalations: "Critical asset · downtime risk · capacity impact.",
    kpi: "Prediction lead time ≥ 72h · precision ≥ 75%" },

  // ─── Quality zone ─────────────────────────────────────────────────────
  { id: "AG-018", zone: "QUAL", name: "CAPA Drafter", defaultLevel: "L1",
    role: "Drafts CAPA from NCR data; suggests root cause framework; links evidence.",
    tools: ["fetch_ncr", "fetch_5why", "draft_capa", "link_evidence"],
    escalations: "Always — final CAPA closure is human (quality manager + legal).",
    pilot: true, kpi: "Draft accepted ≥ 80%" },
  { id: "AG-019", zone: "QUAL", name: "SPC Analyst", defaultLevel: "L2",
    role: "Watches characteristics for trends, drift, special-cause variation.",
    tools: ["query_spc", "calculate_cpk", "flag_special_cause"],
    escalations: "Out-of-control · trend on critical characteristic.",
    kpi: "True-positive rate ≥ 75%" },
  { id: "AG-020", zone: "QUAL", name: "Audit Preparer", defaultLevel: "L1",
    role: "Assembles audit evidence bundles per regulator request.",
    tools: ["fetch_evidence", "validate_completeness", "package_bundle"],
    escalations: "Regulatory submission · 21 CFR Part 11 signatures required.",
    kpi: "Evidence completeness ≥ 100%" },

  // ─── Cloud / Analytics zone ───────────────────────────────────────────
  { id: "AG-021", zone: "CLOUD", name: "Schedule Optimizer", defaultLevel: "L2",
    role: "What-if planning across plants; proposes resequencing & load-balancing.",
    tools: ["run_what_if", "score_schedule", "propose_resequence", "publish_plan"],
    escalations: "Customer-priority change · cross-plant move · multi-day disruption.",
    kpi: "Plant acceptance ≥ 90% · zero promise breaks" },
  { id: "AG-022", zone: "CLOUD", name: "Demand Sensor", defaultLevel: "L3",
    role: "Watches signals (orders, B2B, market data, weather); flags forecast drift.",
    tools: ["query_orders", "ingest_market_signal", "score_forecast", "propose_adjustment"],
    escalations: "Forecast drift > threshold · capacity-affecting · multi-SKU.",
    kpi: "Forecast accuracy lift ≥ 8% MAPE" },
  { id: "AG-023", zone: "CLOUD", name: "Knowledge Bot", defaultLevel: "L3",
    role: "Answers questions from runbooks, BoPs, policies, past incidents; cites sources.",
    tools: ["semantic_search", "cite_source", "ask_clarifying", "escalate_unknown"],
    escalations: "Sensitive topic · policy/safety question · low retrieval confidence.",
    kpi: "Answer cited rate 100% · accuracy ≥ 90%" },
  { id: "AG-024", zone: "CLOUD", name: "KPI Custodian", defaultLevel: "L2",
    role: "Maintains canonical KPI definitions, watches drift, alerts on stale data.",
    tools: ["validate_kpi_def", "check_freshness", "alert_drift"],
    escalations: "Definition change · stakeholder dispute on metric.",
    kpi: "KPI staleness alerts < 24h" },

  // ─── B2B / IT Ops zone ────────────────────────────────────────────────
  { id: "AG-025", zone: "B2B", name: "DLQ Triage", defaultLevel: "L3",
    role: "Classifies dead-letter queue messages; auto-replays safe ones; routes the rest.",
    tools: ["classify_dlq", "auto_replay", "route_to_owner"],
    escalations: "Repeating pattern · regulated data · financial impact · involve humans.",
    kpi: "Replay success ≥ 95%" },
  { id: "AG-026", zone: "B2B", name: "Partner Onboarder", defaultLevel: "L1",
    role: "Drafts partner directory entries; validates certs; tests mapping in sandbox.",
    tools: ["validate_cert", "test_mapping_sandbox", "draft_directory_entry"],
    escalations: "Production-ready signoff · regulatory exchange · cert chain anomaly.",
    kpi: "Onboarding draft time -50%" },
  { id: "AG-027", zone: "B2B", name: "SLA Watchdog", defaultLevel: "L3",
    role: "Continuously watches backbone SLAs; pages on burn-rate; updates dashboards.",
    tools: ["query_otel_traces", "calculate_burn", "page_on_call"],
    escalations: "P1 incident · multi-flow correlation · DR drill needed.",
    kpi: "MTTR ≤ 30 min for P1" },
];

// 12 inviolable human-control points — no agent ever owns these
export const HUMAN_GATES = [
  { num:  1, name: "Safety-critical characteristic limits",
    detail: "Any change to a char marked safety-critical (medical, automotive, aerospace) requires a named human approver." },
  { num:  2, name: "BOM structural changes",
    detail: "Adding, removing, or reordering BOM nodes. Attribute updates can be agentic; structure cannot." },
  { num:  3, name: "Effectivity scope decisions",
    detail: "Which serial numbers, dates, plants, or orders a change applies to. Agent proposes; humans decide." },
  { num:  4, name: "CAPA closure & disposition of critical defects",
    detail: "Final disposition of any critical NCR, and any CAPA that closes it. Quality manager and legal sign-off." },
  { num:  5, name: "Financial postings above threshold",
    detail: "Any journal entry, write-off, or cost adjustment beyond a per-entity threshold. Controller approval." },
  { num:  6, name: "Master data: create + merge",
    detail: "Creating a new material master, or merging two. Touches every downstream system; never agentic." },
  { num:  7, name: "Customer-facing communications",
    detail: "Anything a customer reads — letters, invoices, recall notices, RMAs. Agent drafts; human signs." },
  { num:  8, name: "Regulatory submissions & filings",
    detail: "21 CFR Part 11, ICH, GxP, environmental, financial. Always human-signed; agent assists only." },
  { num:  9, name: "New supplier qualification",
    detail: "Onboarding a partner to production. Agent prepares; supplier-quality team qualifies; legal contracts." },
  { num: 10, name: "Cybersecurity policy & posture changes",
    detail: "Firewall rules, identity policies, secrets rotation cadences. CISO retains authority." },
  { num: 11, name: "Cross-plant resource reallocation",
    detail: "Moving demand, capacity, or critical staff between plants. Plant directors and S&OP." },
  { num: 12, name: "Deploying or changing autonomy of an agent",
    detail: "Meta-level: only humans deploy, promote, demote, or retire agents. Architecture Review Board." },
];

// 3 governance bodies
export const GOVERNANCE_BODIES = [
  { id: "ARB", name: "Architecture Review Board (expanded)",  scope: "existing · scope expanded", color: "#a78bfa",
    decides: [
      "Agent passports · register new agent",
      "Architecture changes affecting >1 zone",
      "Policy bundle changes",
      "Autonomy level promotions (L1→L2, L2→L3)",
      "Tool additions to agent toolkit",
      "Phase gate sign-offs",
    ],
    cadence: "Bi-weekly · ad-hoc for incidents",
    members: "Chief Architect · domain leads · security · AI platform" },
  { id: "AES", name: "Agent Ethics & Safety Committee", scope: "NEW · highest authority", color: "#ff4f8b",
    decides: [
      "L4 and L5 autonomy approvals",
      "Behavioral policy for sensitive agents",
      "Disposition of ethics complaints",
      "Approve / veto controversial use cases",
      "Kill-switch policy & drills",
      "Annual agentic risk report to board",
    ],
    cadence: "Monthly · weekly during pilot · ad-hoc for issues",
    members: "Chief Risk Officer (chair) · ethics SME · legal · ops · independent member" },
  { id: "MRC", name: "Model Risk Committee",            scope: "NEW · technical bar",  color: "#ffb300",
    decides: [
      "Drift thresholds and responses",
      "Eval pass/fail thresholds",
      "Model vendor selection & exits",
      "Hallucination & accuracy targets per agent",
      "Incident classifications",
      "Cost & rate-limit ceilings",
    ],
    cadence: "Weekly · daily during incidents · monthly post-stabilization",
    members: "AI Platform lead · eval eng · agent SRE lead · finance · risk" },
];

// 8 KPIs: 4 agent-level + 4 program-level
export const AGENTIC_KPIS = [
  { type: "agent",   name: "AGENT SUCCESS RATE",    color: "#00d4c0",
    defn: "% of tasks completed without escalation or rework",
    target: "≥95% steady-state (per agent class)",
    watch: "Below threshold = redesign · sudden drop = model drift" },
  { type: "agent",   name: "ESCALATION RATE",       color: "#00d4c0",
    defn: "% of tasks where agent hands to a human",
    target: "5–15% (too low = overreach · too high = useless)",
    watch: "Trend matters more than absolute · per-agent baseline" },
  { type: "agent",   name: "AUTONOMY ACTUALS",      color: "#00d4c0",
    defn: "Actual proportion of agent action vs human, vs declared level",
    target: "Match declared level ±10%",
    watch: "Drift below = humans not trusting · above = policy holes" },
  { type: "agent",   name: "COST PER TASK",         color: "#00d4c0",
    defn: "Token + tool-call cost averaged per completed task",
    target: "Stable trend · improving with prompt iteration",
    watch: "Spikes = inefficient loops · check eval set" },
  { type: "program", name: "HUMAN TIME SAVED",      color: "#38a8ff",
    defn: "FTE-hours freed up across agentic workflows",
    target: "Cumulative · ramp to 10+ FTE by month 18",
    watch: "Self-reported + measured · validates ROI thesis" },
  { type: "program", name: "AUDIT/EVAL PASS RATE",  color: "#00ff88",
    defn: "% of golden cases passed by current agent fleet",
    target: "≥98% across fleet · per-agent breakdowns",
    watch: "Per-quarter trend · drift indicator" },
  { type: "program", name: "INCIDENT MTTR",         color: "#ffb300",
    defn: "Mean time to detect + diagnose + resolve agent incident",
    target: "≤2h for high-sev · ≤30min for kill-switch use",
    watch: "Decreasing = playbooks working · increasing = process outgrown" },
  { type: "program", name: "OPERATOR NPS",          color: "#ff4f8b",
    defn: "Floor + functional team satisfaction with agents",
    target: "Net Promoter ≥40 · trending up · per-zone",
    watch: "Tells you adoption health · low NPS = ignore at peril" },
];

// 5 implementation phases (24 months)
export const PHASES = [
  { id: "P0", name: "FOUNDATION",     months: "M1-3",   color: "#ffb300",
    scope: [
      "Agent IAM (SPIFFE/SPIRE)",
      "Policy engine (OPA) deployed",
      "Audit ledger online",
      "Eval harness + golden sets",
      "Agent registry + passport",
      "Approval console MVP",
    ],
    gate: "Platform pen-tested · evals signed off · ARB charter ratified" },
  { id: "P1", name: "FIRST PILOTS",   months: "M4-6",   color: "#38a8ff",
    scope: [
      "3 L1 agents shadow-run: WI Gen, MDM Steward, CAPA Drafter",
      "No write tools enabled",
      "Human reviews every output",
      "Daily eval comparison: agent vs human",
      "Operator feedback loops",
    ],
    gate: "≥85% draft acceptance · zero quality regressions · ops sign-off" },
  { id: "P2", name: "L2 EXPANSION",   months: "M7-12",  color: "#00d4c0",
    scope: [
      "12 L2 agents across all zones",
      "Write tools enabled with policy gating",
      "Approval workflows in production",
      "Plant-by-plant rollout",
      "Cost & rate limits enforced",
    ],
    gate: "Per-agent success ≥95% · escalation rate in band · cost per task on target" },
  { id: "P3", name: "L3 GRADUATES",   months: "M13-18", color: "#00ff88",
    scope: [
      "Promote proven L2s to L3 case-by-case",
      "Cross-zone orchestration matures",
      "Supervisor agent active",
      "Multi-plant deployment",
      "Cost optimization agents added",
    ],
    gate: "≥6mo track record · ARB review per promotion · operator buy-in" },
  { id: "P4", name: "SCALE + OPT",    months: "M19-24", color: "#34d399",
    scope: [
      "Multi-plant standard pattern",
      "Cross-agent collaboration",
      "Energy + sustainability agents",
      "Knowledge graph integration",
      "Continuous improvement automation",
    ],
    gate: "Steady-state metrics · KPI targets met · all-zone coverage achieved" },
];

// Kill-switch model — 4 levels
export const KILL_SWITCH = [
  { level: "PAUSE",   color: "#ffd600",
    when:    "Investigating anomaly · planned maintenance · suspicious pattern",
    who:     "Service owner · domain coordinator",
    time:    "Instant",
    reverse: "Resume button" },
  { level: "STOP",    color: "#ff4444",
    when:    "Active incident · data quality breach · security flag",
    who:     "Service owner · SRE on-call",
    time:    "≤30 seconds",
    reverse: "Manual restart after RCA" },
  { level: "REVERT",  color: "#ff4f8b",
    when:    "Wrong action committed · audit found bad output",
    who:     "Service owner + change board",
    time:    "Minutes to hours",
    reverse: "Forward-fix patch + redeploy" },
  { level: "DISABLE", color: "#ff4f8b",
    when:    "Material defect · ethics issue · sunset · replaced",
    who:     "ARB + service owner",
    time:    "Permanent until redeploy",
    reverse: "Full re-review + new deployment" },
];
